<template>
    <div class="headerSurveys">
      <h1 class="list-title">{{ $t('surveys.label.list.title') }}</h1>
      <v-data-table
        :headers="headers"
        :items="surveys"
        :header-props="{ class: 'red-header' }"
        :search="searchTerm"
        :no-data-text="$t('surveys.label.list.noData')"
        :footer-props="{ 'items-per-page-text': $t('label.rowsPerPage.title') }"
        :loading="loading"
      >
        <template v-slot:top>
          <div style="margin-bottom: 50px">
            <div class="row justify-space-between mx-auto">
              <v-text-field
                :label="$t('surveys.label.list.search')"
                :placeholder="$t('surveys.label.list.searchPlaceholder')"
                append-icon="mdi-magnify"
                dense
                outlined
                class="col-md-5"
                @input="search"
              ></v-text-field>

              <div class="btn">
                <v-btn
                  color="primary"
                  @click="createSurvey"
                >
                  <v-icon left>mdi-plus</v-icon>
                  {{ $t('surveys.label.list.newSurvey') }}
                </v-btn>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:[`item.title`]="{ item }">
          <div class="survey-title">
            <span class="custom-font">{{ item.title }}</span>
            <span class="survey-title__created">{{ $t('surveys.label.list.createdAtPrefix') }} {{ formatDate(item.createdAt) }}</span>
          </div>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            :class="['status-chip', getStatusClass(item.status)]"
            small
          >
            {{ getStatusLabel(item.status) }}
          </v-chip>
        </template>

        <template v-slot:[`item.questionCount`]="{ item }">
          <span class="custom-font">{{ item.questionCount }}</span>
        </template>

        <template v-slot:[`item.responseCount`]="{ item }">
          <span class="custom-font">{{ item.responseCount }}</span>
        </template>

        <template v-slot:[`item.startDate`]="{ item }">
          <span class="custom-font">{{ formatDate(getSurveyField(item, 'startDate', 'validFrom', 'startAt')) }}</span>
        </template>

        <template v-slot:[`item.validUntil`]="{ item }">
          <span class="custom-font">{{ formatDate(getSurveyField(item, 'validUntil', 'endDate', 'endAt', 'validTo')) }}</span>
        </template>

        <template v-slot:[`item.action`]="{ item }">
          <div class="actions">
            <v-btn
              icon
              small
              @click="viewSurvey(item)"
              :title="$t('surveys.label.list.actions.view')"
            >
              <v-icon color="primary">mdi-eye</v-icon>
            </v-btn>

            <v-btn
              icon
              small
              @click="viewResults(item)"
              :title="$t('surveys.label.list.actions.results')"
            >
              <v-icon color="primary">mdi-chart-bar</v-icon>
            </v-btn>

            <v-btn
              icon
              small
              @click="editSurvey(item)"
              :title="$t('surveys.label.list.actions.edit')"
              :disabled="item.status === 'CLOSED' || item.status === 'PUBLISHED'"
            >
              <v-icon color="primary">mdi-pencil</v-icon>
            </v-btn>

            <v-btn
              icon
              small
              @click="finalizeSurvey(item)"
              :title="$t('surveys.label.list.actions.finalize')"
              :disabled="item.status !== 'PUBLISHED'"
            >
              <v-icon color="primary">mdi-stop-circle</v-icon>
            </v-btn>
          </div>
        </template>

      </v-data-table>
    </div>
</template>

<script>
import EventBus from '@/utils/event_bus.js';
import { mapActions, mapGetters } from "vuex";
import Swal from "sweetalert2";

import {
    ACTION_GET_SURVEYS,
    ACTION_PATCH_SURVEY_STATUS,
    GET_SURVEYS,
    GET_STATE_LOADING,
    SURVEYS_M,
} from "@/views/surveys/store/types/types.js";

export default {
    name: "listSurveysComponent",

    data() {
        return {
            searchTerm: "",
        };
    },

    async mounted() {
        await this.getActionSurveys();

        EventBus.$on('survey-created-successfully', async () => {
            await this.reloadSurveys();
        });

        EventBus.$on('survey-updated-successfully', async () => {
            await this.reloadSurveys();
        });
    },

    methods: {
        ...mapActions({
            getActionSurveys: `${SURVEYS_M}${ACTION_GET_SURVEYS}`,
            patchStatusActionSurvey: `${SURVEYS_M}${ACTION_PATCH_SURVEY_STATUS}`,
        }),

        async reloadSurveys() {
            await this.getActionSurveys();
        },

        search(value) {
            this.searchTerm = value;
        },

        createSurvey() {
            this.$router.push({ name: 'surveys-create' });
        },

        viewSurvey(item) {
            this.$router.push({ name: 'surveys-create', query: { id: item.surveyId, mode: 'view' } });
        },

        viewResults(item) {
            this.$router.push({ name: 'surveys-results', query: { id: item.surveyId } });
        },

        editSurvey(item) {
            this.$router.push({ name: 'surveys-create', query: { id: item.surveyId, mode: 'edit' } });
        },

        async finalizeSurvey(item) {
            const result = await Swal.fire({
                title: this.$t('surveys.label.list.finalize.title'),
                text: this.$t('surveys.label.list.finalize.text'),
                icon: "question",
                showCancelButton: true,
                confirmButtonText: this.$t('surveys.label.list.finalize.confirm'),
                cancelButtonText: 'Cancelar',
                confirmButtonColor: "#6E17A5"
            });

            if (result.isConfirmed) {
                await this.patchStatusActionSurvey({ surveyId: item.surveyId, status: 'CLOSED' });
            }
        },

        getStatusClass(status) {
            switch (status) {
                case 'PUBLISHED': return 'status-chip--published';
                case 'DRAFT': return 'status-chip--draft';
                case 'CLOSED': return 'status-chip--closed';
                default: return 'status-chip--draft';
            }
        },

        getStatusLabel(status) {
            const key = `surveys.status.${status}`;
            const translated = this.$t(key);
            return translated !== key ? translated : status;
        },

        formatDate(value) {
            if (!value) return '-';
            return new Date(value).toLocaleDateString('es-CO');
        },

        getSurveyField(item, ...fields) {
            for (const field of fields) {
                if (item[field] !== undefined && item[field] !== null) return item[field];
            }
            return null;
        }
    },

    computed: {
        ...mapGetters({
            surveys: `${SURVEYS_M}${GET_SURVEYS}`,
            loading: `${SURVEYS_M}${GET_STATE_LOADING}`,
        }),

        headers() {
            return [
                {
                    text: this.$t('surveys.label.table.name'),
                    value: "title",
                    align: "center",
                    sortable: false,
                    class: "primary--text custom-header font-weight-medium text-subtitle-1",
                },
                {
                    text: this.$t('surveys.label.table.status'),
                    value: "status",
                    align: "center",
                    sortable: false,
                    class: "primary--text custom-header font-weight-medium text-subtitle-1",
                },
                {
                    text: this.$t('surveys.label.table.questions'),
                    value: "questionCount",
                    align: "center",
                    sortable: false,
                    class: "primary--text custom-header font-weight-medium text-subtitle-1",
                },
                {
                    text: this.$t('surveys.label.table.responses'),
                    value: "responseCount",
                    sortable: false,
                    align: "center",
                    class: "primary--text custom-header font-weight-medium text-subtitle-1",
                },
                {
                    text: this.$t('surveys.label.table.startDate'),
                    value: "startDate",
                    sortable: false,
                    align: "center",
                    class: "primary--text custom-header font-weight-medium text-subtitle-1",
                },
                {
                    text: this.$t('surveys.label.table.validUntil'),
                    value: "validUntil",
                    sortable: false,
                    align: "center",
                    class: "primary--text custom-header font-weight-medium text-subtitle-1",
                },
                {
                    text: this.$t('surveys.label.table.actions'),
                    value: "action",
                    sortable: false,
                    align: "center",
                    class: "primary--text custom-header font-weight-medium text-subtitle-1",
                },
            ];
        },
    },
};
</script>

<style lang="css" scoped>
    .headerSurveys h1,
    .list-title {
        margin-bottom: 30px;
        color: #6E17A5;
    }

    .headerSurveys {
        display: flex;
        gap: 40px;
        flex-direction: column;
        margin-top: 20px;
        margin-left: 20px;
    }

    .actions {
        display: flex;
        justify-content: center;
        gap: 4px;
    }

    .survey-title {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .survey-title__created {
        font-size: 12px;
        color: #888;
        margin-top: 2px;
    }

    .custom-font {
        font-size: 15px;
    }

    .status-chip {
        font-size: 13px;
        font-weight: 500;
        border-radius: 12px;
        opacity: 0.9;
    }

    .status-chip--published {
        background-color: rgba(76, 175, 80, 0.15) !important;
        color: #2e7d32 !important;
    }

    .status-chip--draft {
        background-color: rgba(158, 158, 158, 0.15) !important;
        color: #616161 !important;
    }

    .status-chip--closed {
        background-color: rgba(244, 67, 54, 0.15) !important;
        color: #c62828 !important;
    }

    h1, h2, h3, h4, h5, h6 {
        font-family: "Amsi Pro", sans-serif;
        color: #6E17A5;
        font-weight: normal;
    }
</style>
